import type { CentrixExtractParameters } from "core/entities/CentrixTypes";
import type { HttpRequestMethod } from "core/entities/UtilityTypes";
import type { paths } from "resources/api-schema";
import { buildUrl } from "./buildUrl";

/**
 * Builds a URL based on the provided route and parameters.
 * @param route - a route defined in `api-schema.d.ts` to be appended to the base URL. Can contain placeholders.
 * @param parameters - The parameters to be included in the URL. parameters are defined in `api-schema.d.ts` Can include path and query parameters.
 * @returns The constructed URL string.
 * @example
 * ```typescript
 *  const url = buildUrl({
 *    route: "/api/person/{id}/group/{groupId}",
 *    parameters: {
 *      path: { id: "123", groupId: "456" },
 *      query: { filter: "active" },
 *    },
 *  });
 *  console.log(url) //"https://api.example.com/api/person/123/group/456?filter=active"
 * ```
 */
export function buildCentrixUrl<
  T extends keyof paths,
  M extends HttpRequestMethod = "get",
>({
  path,
  parameters,
  locale,
  apiUrl,
}: {
  path: T;
  parameters: CentrixExtractParameters<T, M> | undefined;
  locale?: string;
  apiUrl: string;
}) {
  return buildUrl<paths, T, M>({ path, parameters, locale, baseUrl: apiUrl });
}
